import React from "react";
import "./PrivacyPolicyPage.css";
import HeaderMenu from '../Utils/Header';
import Footer from '../Utils/Footer';
import RouteTracker from '../Utils/RouteTracker';

const translations = {
  en: {
    title: "Privacy Policy",
    lastUpdated: "Last updated: January 1, 2025",
    introduction: "Introduction",
    introText: "At Kubitu's Imóveis, we value your privacy. This document explains how we collect, use, and protect your personal information.",
    infoCollected: "Information We Collect",
    infoCollectedText: "We collect personal information in various ways, including:",
    infoCollectedList: [
      "Information provided directly by you, such as name and email.",
      "Data collected automatically through cookies and other technologies."
    ],
    howWeUse: "How We Use Your Information",
    howWeUseText: "We use the collected information to:",
    howWeUseList: [
      "Provide services and support.",
      "Improve user experience.",
      "Ensure compliance with legal obligations."
    ],
    sharingInfo: "Sharing Information",
    sharingInfoText: "We do not share your personal information with third parties, except when necessary to comply with legal requirements or provide services.",
    yourRights: "Your Rights",
    yourRightsText: "You have the right to:",
    yourRightsList: [
      "Access your personal information.",
      "Request corrections or deletions.",
      "Withdraw your consent for data use."
    ],
    contact: "Contact",
    contactText: "For questions or concerns related to our privacy policy, contact us at:",
    email: "Email",
  },
  pt: {
    title: "Política de Privacidade",
    lastUpdated: "Última atualização: 01 de Janeiro de 2025",
    introduction: "Introdução",
    introText: "Na Kubitu's Imóveis, valorizamos a sua privacidade. Este documento explica como recolhemos, utilizamos e protegemos as suas informações pessoais.",
    infoCollected: "Informações que Recolhemos",
    infoCollectedText: "Recolhemos informações pessoais de várias formas, incluindo:",
    infoCollectedList: [
      "Informações fornecidas diretamente por si, como nome e email.",
      "Dados recolhidos automaticamente através de cookies e outras tecnologias."
    ],
    howWeUse: "Como Usamos Suas Informações",
    howWeUseText: "Utilizamos as informações recolhidas para:",
    howWeUseList: [
      "Fornecer serviços e suporte.",
      "Melhorar a experiência do utilizador.",
      "Garantir conformidade com obrigações legais."
    ],
    sharingInfo: "Partilha de Informações",
    sharingInfoText: "Não partilhamos as suas informações pessoais com terceiros, exceto quando necessário para cumprir requisitos legais ou fornecer serviços.",
    yourRights: "Os Seus Direitos",
    yourRightsText: "Tem o direito de:",
    yourRightsList: [
      "Aceder às suas informações pessoais.",
      "Solicitar correções ou exclusões.",
      "Retirar o seu consentimento para o uso de dados."
    ],
    contact: "Contacto",
    contactText: "Para dúvidas ou preocupações relacionadas com a nossa política de privacidade, contacte-nos em:",
    email: "Email",
  },
  fr: {
    title: "Politique de Confidentialité",
    lastUpdated: "Dernière mise à jour : 1er janvier 2025",
    introduction: "Introduction",
    introText: "Chez Kubitu's Imóveis, nous accordons de l'importance à votre vie privée. Ce document explique comment nous collectons, utilisons et protégeons vos informations personnelles.",
    infoCollected: "Informations que Nous Collectons",
    infoCollectedText: "Nous collectons des informations personnelles de différentes manières, notamment :",
    infoCollectedList: [
      "Informations fournies directement par vous, telles que le nom et l'email.",
      "Données collectées automatiquement via des cookies et d'autres technologies."
    ],
    howWeUse: "Comment Nous Utilisons Vos Informations",
    howWeUseText: "Nous utilisons les informations collectées pour :",
    howWeUseList: [
      "Fournir des services et un support.",
      "Améliorer l'expérience utilisateur.",
      "Assurer la conformité avec les obligations légales."
    ],
    sharingInfo: "Partage d'Informations",
    sharingInfoText: "Nous ne partageons pas vos informations personnelles avec des tiers, sauf lorsque cela est nécessaire pour se conformer aux exigences légales ou fournir des services.",
    yourRights: "Vos Droits",
    yourRightsText: "Vous avez le droit de :",
    yourRightsList: [
      "Accéder à vos informations personnelles.",
      "Demander des corrections ou des suppressions.",
      "Retirer votre consentement à l'utilisation des données."
    ],
    contact: "Contact",
    contactText: "Pour toute question ou préoccupation concernant notre politique de confidentialité, contactez-nous à :",
    email: "Email",
  },
};

const PrivacyPolicyPage = () => {
  const [language, setLanguage] = React.useState(localStorage.getItem("language") || "pt");
  const t = translations[language];

  React.useEffect(() => {
    const handleLanguageChange = () => {
      setLanguage(localStorage.getItem("language") || "pt");
    };
    window.addEventListener("languageChange", handleLanguageChange);
    return () => window.removeEventListener("languageChange", handleLanguageChange);
  }, []);

  return (
    <>
      <HeaderMenu />
      <RouteTracker />
      <div className="privacy-policy-container">
        {/* Header Section */}
        <header className="privacy-header">
          <h1>{t.title}</h1>
          <p>{t.lastUpdated}</p>
        </header>

        {/* Content Section */}
        <section className="privacy-content">
          <h2>{t.introduction}</h2>
          <p>{t.introText}</p>

          <h2>{t.infoCollected}</h2>
          <p>{t.infoCollectedText}</p>
          <ul>
            {t.infoCollectedList.map((item, index) => (
              <li key={index}>{item}</li>
            ))}
          </ul>

          <h2>{t.howWeUse}</h2>
          <p>{t.howWeUseText}</p>
          <ul>
            {t.howWeUseList.map((item, index) => (
              <li key={index}>{item}</li>
            ))}
          </ul>

          <h2>{t.sharingInfo}</h2>
          <p>{t.sharingInfoText}</p>

          <h2>{t.yourRights}</h2>
          <p>{t.yourRightsText}</p>
          <ul>
            {t.yourRightsList.map((item, index) => (
              <li key={index}>{item}</li>
            ))}
          </ul>

          <h2>{t.contact}</h2>
          <p>
            {t.contactText}
            <br />
            <strong>{t.email}:</strong>{" "}
            <a href="mailto:kubitusimoveis@gmail.com">kubitusimoveis@gmail.com</a>
          </p>
        </section>
      </div>
      <Footer />
    </>
  );
};

export default PrivacyPolicyPage;